import { useGetRecoveryStatusQuery } from 'api/recoveryAi'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { RECOVERY_AI_ROOT } from 'scenes/RecoveryAI/routes'
import { useAbility } from 'utilities/ability'

const t = buildTranslate('home')

// The Orders 👍/👎 REPORT (Looker aggregate) lives at /order_ratings, distinct
// from /order_feedback (the Overview/Feedback/Settings page). This tile is
// framed as a report/metric CTA ("Explore order satisfaction"), so it points
// at the report's address.
export const ORDER_FEEDBACK_URL = '/order_ratings'

export type FeedbackTiles = {
  showReadTile: boolean
  readLabel: string
  readUrl: string
  reportLabel: string
  reportUrl: string
}

/**
 * FR-65 label + destination for the two Home feedback tiles, derived once and
 * shared by ButtonRow (legacy roles) and RbacButtonRow (RBAC) so the two rows
 * can't drift apart. Both destinations are unconditional — the retired NPS
 * pages (/responses, /reports/feedback) are never linked from the dashboard:
 *
 *  - Read tile → the RecoveryAI homepage (RECOVERY_AI_ROOT). Its label is the
 *    RecoveryAI CTA: "Explore RecoveryAI" before activation, "See RecoveryAI
 *    performance" once active.
 *  - Report tile → the Order ratings report (ORDER_FEEDBACK_URL), labelled
 *    "Explore order satisfaction".
 *
 * The read label's activation state comes from the UNGATED recovery/status
 * probe — the same signal the Operations Center nav reads — NOT
 * useGetRecoveryConfigQuery, which is skipped without MerchantSettings and so
 * would mislabel an FR-39 explorer ("Explore RecoveryAI") at an already-active
 * merchant. The probe is skipped while the recovery-ai flag is off.
 *
 * `showReadTile` gates the read tile's visibility so it never dead-ends. The
 * tile shows only where /recovery_ai actually resolves for this viewer: an
 * un-set-up merchant's page is open marketing copy (any viewer), but once the
 * merchant is configured /recovery_ai is the live console, gated on
 * MerchantSettings (see scenes/RecoveryAI/index.tsx) — so a configured
 * merchant's viewer without MerchantSettings would be redirected to "/". This
 * mirrors the Operations Center nav's recovery gate, which disables the same
 * link for the same viewer; an in-flight or unavailable probe resolves to
 * shown, matching the nav (at most one early click the route then redirects,
 * versus a flashed-then-hidden tile for everyone). The flag-off case hides it.
 */
export const useFeedbackTiles = (): FeedbackTiles => {
  const ability = useAbility()
  const recoveryAiEnabled = useFlag('recovery-ai', false)
  const { data: recoveryStatus } = useGetRecoveryStatusQuery(undefined, {
    skip: !recoveryAiEnabled,
  })
  const recoveryConfigured = recoveryStatus?.configured ?? false
  const recoveryActive = recoveryStatus?.active ?? false
  // /recovery_ai is MerchantSettings-gated once the merchant is configured; the
  // un-set-up page is open to everyone. Match the route's own gate (access, per
  // ProtectedRoute) so the tile isn't a dead-end.
  const canAccessRecoveryConsole = ability.can('access', 'MerchantSettings')

  return {
    showReadTile:
      recoveryAiEnabled && (!recoveryConfigured || canAccessRecoveryConsole),
    readLabel: t(
      recoveryActive
        ? 'buttons.recovery_ai_performance'
        : 'buttons.explore_recovery_ai'
    ),
    readUrl: RECOVERY_AI_ROOT,
    reportLabel: t('buttons.feedback_report'),
    reportUrl: ORDER_FEEDBACK_URL,
  }
}
